<script setup>
import { computed, nextTick, onBeforeUpdate, onMounted, ref } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  fields: {
    type: Number,
    default: 6,
  },
  inputMode: {
    type: String,
    default: 'numeric',
  },
  inputType: {
    type: String,
    default: 'number',
  },
  fieldWidth: {
    type: Number,
    default: 35,
  },
  fieldHeight: {
    type: Number,
    default: 35,
  },
  autoFocus: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['complete', 'change', 'update:modelValue']);

const BACKSPACE = 8;
const LEFT_ARROW = 37;
const RIGHT_ARROW = 39;
const DELETE = 46;

const otp = ref([]);
const activeInput = ref(0);
let itemRefs = [];

const inputTypeValue = computed(() =>
  ['text', 'number'].includes(props.inputType)
    ? 'text'
    : props.inputType,
);

const setItemRef = (el) => {
  if (el) {
    itemRefs.push(el);
  }
};

const handleFocus = (index) => {
  activeInput.value = index;
};
const focusInput = (input) => {
  activeInput.value = Math.max(Math.min(props.fields - 1, input), 0);
  itemRefs[activeInput.value].focus();
  itemRefs[activeInput.value].select();
};
const focusNextInput = () => {
  if (activeInput.value + 1 <= props.fields - 1) {
    focusInput(activeInput.value + 1);
  }
};
const focusPrevInput = () => {
  focusInput(activeInput.value - 1);
};

const handlerChange = (value) => {
  let val = value.target.value;
  if (props.inputType === 'number' && val.replace(/[^\d]/gi, '') === '') {
    otp.value[activeInput.value] = null;
    nextTick(() => {
      otp.value[activeInput.value] = '';
    });
  } else {
    handleChange(val);
  }


};
const handleChange = (value) => {
  changeCodeAtFocus(value);
  focusNextInput();
};
const changeCodeAtFocus = (value) => {
  otp.value[activeInput.value] = value;
  emit('update:modelValue', otp.value.join(''));
  emit('change', otp.value.join(''));
  checkFilledAllInputs();
};
const checkFilledAllInputs = () => {
  if (otp.value.join('').length === props.fields) {
    emit('update:modelValue', otp.value.join(''));
    emit('complete', otp.value.join(''));
  }
};
const handleKeyDown = (event) => {
  switch (event.keyCode) {
    case BACKSPACE:
      event.preventDefault();
      changeCodeAtFocus('');
      focusPrevInput();
      break;
    case DELETE:
      event.preventDefault();
      changeCodeAtFocus('');
      break;
    case LEFT_ARROW:
      event.preventDefault();
      focusPrevInput();
      break;
    case RIGHT_ARROW:
      event.preventDefault();
      focusNextInput();
      break;
    default:
      break;
  }
};
const handleOnPaste = (event) => {
  event.preventDefault();
  const pastedData = event.clipboardData
    .getData('text/plain')
    .slice(0, props.fields - activeInput.value)
    .split('');

  if ((props.inputType === 'number' ? !!pastedData.join('').match(/^\d+$/) : true)) {
    const currentCharsInOtp = otp.value.slice(0, activeInput.value);
    const combinedWithPastedData = currentCharsInOtp.concat(pastedData);

    combinedWithPastedData.slice(0, props.fields).forEach(function(value, i) {
      otp.value[i] = value;
    });

    focusInput(combinedWithPastedData.slice(0, props.fields).length);
    return checkFilledAllInputs();
  }
};

onBeforeUpdate(() => {
  itemRefs = [];
});

onMounted(() => {
  if (props.autoFocus) {
    itemRefs[activeInput.value].focus();
    itemRefs[activeInput.value].select();
  }
});
</script>

<template>
  <div class="tfa-code-input">
    <template
      v-for="(item, index) in fields"
      :key="index"
    >
      <input
        :ref="setItemRef"
        :disabled="disabled"
        :inputmode="inputMode"
        :style="{width: `${fieldWidth}px`, height: `${fieldHeight}px`}"
        :type="inputTypeValue"
        :value="otp[index]"
        class="tfa-code-input__element"
        max="9"
        maxlength="1"
        min="0"
        pattern="[0-9]"
        @focus="handleFocus(index)"
        @input="handlerChange"
        @keydown="handleKeyDown"
        @paste="handleOnPaste"
      >
    </template>
  </div>
</template>

<style lang="scss">
.tfa-code-input {
  font-family: Open Sans, sans-serif;

  &__element {
    width: 35px;
    height: 35px;
    border: 1px solid #d9d9d9;
    caret-color: #000;
    padding: 5px;
    text-align: center;
    outline: none;
    color: #303634;
    font-size: 20px;
    border-right: none;

    &:focus + input {
      border-left: 1px solid var(--active-color);
    }

    &:focus {
      border: 1px solid var(--active-color);
      border-right: none;
    }

    &:first-child {
      border-radius: 8px 0 0 8px;
    }

    &:last-child {
      border-radius: 0 8px 8px 0;
      border-right: 1px solid #d9d9d9;

      &:focus {
        border-right: 1px solid var(--active-color);
      }
    }
  }

  @at-root .vue3-cascade-1 .vue3-cascade-2 & {
    &__element {
      width: 35px;
      height: 35px;
      border: 1px solid #d9d9d9;
      caret-color: #000;
      padding: 5px !important;
      text-align: center;
      outline: none;
      color: #303634;
      font-size: 20px !important;
      border-right: none;

      &:focus + input {
        border-left: 1px solid var(--active-color) !important;
      }

      &:focus {
        border: 1px solid var(--active-color) !important;
        border-right: none !important;
      }

      &:first-child {
        border-radius: 8px 0 0 8px !important;
      }

      &:last-child {
        border-radius: 0 8px 8px 0 !important;
        border-right: 1px solid #d9d9d9 !important;

        &:focus {
          border-right: 1px solid var(--active-color) !important;
        }
      }
    }
  }
}
</style>